import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { sendOtp, verifyOtp } from '../redux/actions/login'

const RESEND_COOLDOWN_SECONDS = 60
const MAX_RESEND_COUNT = 2

const useOtpVerification = ({ process = 'signup' } = {}) => {
    const dispatch = useDispatch()

    const [otp, setOtp] = useState('')
    const [otpSent, setOtpSent] = useState(false)
    const [otpLoading, setOtpLoading] = useState(false)
    const [otpError, setOtpError] = useState('')
    const [resendCooldown, setResendCooldown] = useState(0)
    const [resendCount, setResendCount] = useState(0)
    const [otpSentFor, setOtpSentFor] = useState(null)  // { method, email, phone }

    const cooldownTimerRef = useRef(null)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)
        }
    }, [])

    // Cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return
        cooldownTimerRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownTimerRef.current)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
        return () => clearInterval(cooldownTimerRef.current)
    }, [resendCooldown])

    // Derived
    const canResend = !otpLoading && resendCooldown === 0 && resendCount < MAX_RESEND_COUNT
    const resendLimitReached = resendCount >= MAX_RESEND_COUNT

    // Build dynamic payload — email priority for display
    // Send both email and phone when available
    const buildPayload = useCallback((email, phone) => {
        const hasEmail = email && email.trim() !== ''
        const hasPhone = phone && phone.trim() !== ''

        // Guard: at least one must be present
        if (!hasEmail && !hasPhone) return null

        const payload = { process }

        if (hasEmail) payload.email = email.trim()
        if (hasPhone) payload.phone = phone.trim()

        // method is used only for UI display ("OTP sent to your email/phone")
        // Priority: email > phone — for display purpose only
        const method = hasEmail ? 'email' : 'phone'
        const value = hasEmail ? email.trim() : phone.trim()

        return { payload, method, value }
    }, [process])

    // OTP input — digits only, max 6
    const handleOtpChange = useCallback((value) => {
        const cleaned = String(value || '').replace(/\D/g, '').slice(0, 6)
        setOtp(cleaned)
        setOtpError('')
    }, [])

    // Send OTP
    const handleSendOtp = useCallback((email, phone) => {
        const built = buildPayload(email, phone)
        if (!built) {
            setOtpError('Please enter email or phone number first.')
            return Promise.resolve(false)
        }

        setOtpLoading(true)
        setOtpError('')

        return dispatch(sendOtp(built.payload)).then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                setOtpSent(true)
                setOtpLoading(false)
                setOtpSentFor({
                    method: built.method,
                    email: built.payload.email || '',
                    phone: built.payload.phone || '',
                })
                setResendCooldown(RESEND_COOLDOWN_SECONDS)
                return true
            } else {
                setOtpLoading(false)
                setOtpError(res?.errors?.message || res?.message || 'Failed to send OTP. Please try again.')
                return false
            }
        }).catch(() => {
            setOtpLoading(false)
            setOtpError('Failed to send OTP. Please try again.')
            return false
        })
    }, [dispatch, buildPayload])

    // Resend OTP
    const handleResendOtp = useCallback((email, phone) => {
        if (!canResend) return Promise.resolve(false)
        setResendCount((prev) => prev + 1)
        setOtp('')
        return handleSendOtp(email, phone)
    }, [canResend, handleSendOtp])

    // Verify OTP (used by permit booking signup step)
    const handleVerifyOtp = useCallback((email, phone) => {
        if (!otp || otp.length !== 6) {
            setOtpError('Please enter the 6-digit OTP.')
            return Promise.resolve(false)
        }

        const built = buildPayload(email, phone)
        if (!built) {
            setOtpError('Please enter email or phone number first.')
            return Promise.resolve(false)
        }

        if (
            otpSentFor &&
            (
                (email || '').trim() !== (otpSentFor.email || '') ||
                (phone || '').trim() !== (otpSentFor.phone || '')
            )
        ) {
            setOtpError('Email or phone changed. Please resend the OTP.')
            return Promise.resolve(false)
        }

        setOtpLoading(true)
        setOtpError('')

        return dispatch(verifyOtp({ ...built.payload, otp })).then((res) => {
            setOtpLoading(false)
            if (res && (res.status === 200 || res.status === 201)) {
                return true
            }
            setOtpError(res?.errors?.message || res?.message || 'Invalid OTP. Please try again.')
            return false
        }).catch(() => {
            setOtpLoading(false)
            setOtpError('Invalid OTP. Please try again.')
            return false
        })
    }, [dispatch, otp, otpSentFor, buildPayload])

    // Reset all state
    const resetOtp = useCallback(() => {
        if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)
        setOtp('')
        setOtpSent(false)
        setOtpLoading(false)
        setOtpError('')
        setResendCount(0)
        setResendCooldown(0)
        setOtpSentFor(null)
    }, [])

    // Reset OTP state when email or phone changes after OTP was sent
    const checkIdentifierChange = useCallback((email, phone) => {
        if (!otpSent || !otpSentFor) return false
        const normalizedEmail = (email || '').trim()
        const normalizedPhone = (phone || '').trim()
        if (
            normalizedEmail !== (otpSentFor.email || '') ||
            normalizedPhone !== (otpSentFor.phone || '')
        ) {
            resetOtp()
            return true
        }
        return false
    }, [otpSent, otpSentFor, resetOtp])

    return {
        otp,
        otpSent,
        otpLoading,
        otpError,
        otpSentFor,
        resendCooldown,
        resendCount,
        canResend,
        resendLimitReached,
        maxOtpResend: MAX_RESEND_COUNT,
        handleOtpChange,
        handleSendOtp,
        handleResendOtp,
        handleVerifyOtp,
        resetOtp,
        checkIdentifierChange,
    }
}

export default useOtpVerification
